import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ref, get, set } from 'firebase/database';
import { database } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import useTeamMembers from '../../hooks/useTeamMembers';
import AssessmentQuestionBuilder, { normalizeQuestions } from '../Assessments/AssessmentQuestionBuilder';
import { translateDepartment } from '../../utils/translationUtils';

const STANDARD_INTERVIEW = 'standard';
const AI_INTERVIEW = 'ai-interview';
const ONE_WAY_VIDEO = 'one-way-video';
const TWO_WAY_VOICE = 'two-way-voice';
const MANUAL_QUESTIONS = 'manual';
const AI_GENERATED_QUESTIONS = 'generate';
const HOME_DEPARTMENTS = [
  'Administration', 'Customer Support', 'Engineering', 'Product Management', 'Design / UX', 'Data & Analytics', 'Marketing', 'Sales', 'Finance & Accounting', 'Human Resources', 'Operations', 'IT / Systems', 'Security / InfoSec', 'Legal', 'Compliance', 'Procurement', 'Supply Chain', 'Logistics', 'Manufacturing', 'Quality Assurance', 'Research & Development', 'Business Development', 'Partnerships', 'Strategy', 'Content', 'Communications / PR', 'Community', 'Facilities', 'Real Estate', 'Construction', 'Healthcare', 'Education'
];

/** Build the plain employer settings shared by manual and AI-created live interviews. */
export const twoWayInterviewSettings = (languageMode, interviewerNotes, answerReviewGuide) => ({
  interviewLanguages: languageMode === 'candidate-choice' ? ['en', 'ar'] : [languageMode],
  defaultInterviewLanguage: languageMode === 'ar' ? 'ar' : 'en',
  assessment_prompt: String(interviewerNotes || '').trim(),
  grading_rubric: String(answerReviewGuide || '').trim(),
});

const NewInterviewStep = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [companyUID, setCompanyUID] = useState('');
  const { teamMembers: teamOptions } = useTeamMembers(companyUID);

  const [title, setTitle] = useState('');
  const [stepMode, setStepMode] = useState(STANDARD_INTERVIEW);
  const [aiDeliveryType, setAiDeliveryType] = useState(ONE_WAY_VIDEO);
  const [interviewers, setInterviewers] = useState([]);
  const [duration, setDuration] = useState(60);
  const [departments, setDepartments] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [afterId, setAfterId] = useState('');
  const [afterOptions, setAfterOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [locationType, setLocationType] = useState('virtual');
  const [address, setAddress] = useState('');
  const [questionMode, setQuestionMode] = useState(MANUAL_QUESTIONS);
  const [manualQuestions, setManualQuestions] = useState([]);
  const [numQuestions, setNumQuestions] = useState(5);
  const [languageMode, setLanguageMode] = useState('candidate-choice');
  const [interviewerNotes, setInterviewerNotes] = useState('');
  const [answerReviewGuide, setAnswerReviewGuide] = useState('');

  useEffect(() => {
    setDepartmentOptions(HOME_DEPARTMENTS);
  }, []);

  useEffect(() => {
    if (stepMode === AI_INTERVIEW && questionMode === MANUAL_QUESTIONS && manualQuestions.length === 0) {
      const oneWay = aiDeliveryType === ONE_WAY_VIDEO;
      setManualQuestions(normalizeQuestions([{ type: oneWay ? 'record-video' : 'short-text', text: '', options: null }], oneWay));
    }
  }, [stepMode, questionMode, manualQuestions.length, aiDeliveryType]);

  useEffect(() => {
    if (stepMode !== AI_INTERVIEW) return;
    const oneWay = aiDeliveryType === ONE_WAY_VIDEO;
    setManualQuestions((previous) => previous.map((question) => ({
      ...question,
      type: oneWay ? 'record-video' : 'short-text',
      options: null
    })));
  }, [aiDeliveryType, stepMode]);

  useEffect(() => {
    const resolveCompany = async () => {
      if (!currentUser) return;
      try {
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        if (ownerSnap.exists()) {
          setCompanyUID(currentUser.uid);
          return;
        }
        const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnap.exists()) {
          setCompanyUID(collabSnap.val().companyId);
        }
      } catch (error) {
        console.error('Resolve company failed', error);
      }
    };

    resolveCompany();
  }, [currentUser]);

  useEffect(() => {
    const loadWorkflow = async () => {
      if (!companyUID) return;

      try {
        const translateStepName = (id, fallback) => {
          switch (id) {
            case 'all-applicants-1': return t('substages.allApplicants');
            case 'filter-applicants': return t('substages.fitApplicants');
            case 'offer-sent': return t('substages.offerSent');
            case 'hired-applicants': return t('substages.hired');
            case 'move-to-onboarding': return t('substages.moveToOnboarding');
            default: return fallback;
          }
        };

        const options = [{ id: 'filter-applicants', name: translateStepName('filter-applicants', t('substages.fitApplicants')) }];
        const workflowSnap = await get(ref(database, `companies/${companyUID}/hiringWorkflow`));

        if (workflowSnap.exists()) {
          const workflow = workflowSnap.val() || {};
          Object.entries(workflow).forEach(([, stage]) => {
            const substages = stage?.substages || {};
            Object.entries(substages).forEach(([substageId, substage]) => {
              const blockedIds = ['all-applicants-1', 'filter-applicants', 'offer-sent', 'hired-applicants'];
              if (blockedIds.includes(substageId) || blockedIds.includes(substage?.id)) return;
              options.push({
                id: substageId,
                name: translateStepName(substageId, substage?.name || substage?.title || substageId)
              });
            });
          });
        }

        setAfterOptions(options);
      } catch (error) {
        console.error('Load workflow steps failed', error);
      }
    };

    loadWorkflow();
  }, [companyUID, i18n.language, t]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.department-dropdown') && !event.target.closest('.chip')) {
        setShowDepartmentDropdown(false);
      }
    };

    if (!showDepartmentDropdown) return undefined;

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDepartmentDropdown]);

  const handleToggleInterviewer = (id) => {
    setInterviewers((prev) => (
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    ));
  };

  const sanitizeDepartmentKey = (department) => department.replace(/[.#$/[\]]/g, '_');

  const buildDepartmentsPayload = () => departments.reduce((accumulator, department) => ({
    ...accumulator,
    [sanitizeDepartmentKey(department)]: true
  }), {});

  /** Build the persisted assessment contract for the selected AI delivery type. */
  const buildAiInterviewAssessment = ({ assessmentTitle, questions = [], totalDuration, baseConfig = null }) => ({
    title: assessmentTitle,
    duration_minutes: aiDeliveryType === TWO_WAY_VOICE
      ? Math.min(20, Math.max(1, Number(totalDuration) || 20))
      : (Number(totalDuration) || 30),
    questions: normalizeQuestions(questions, aiDeliveryType === ONE_WAY_VIDEO).slice(0, aiDeliveryType === TWO_WAY_VOICE ? 6 : undefined),
    mode: 'ai-interview',
    type: 'ai-interview',
    deliveryType: aiDeliveryType,
    answerFormat: aiDeliveryType === TWO_WAY_VOICE ? 'live-voice' : 'record-video',
    ...(aiDeliveryType === TWO_WAY_VOICE ? {
      varyByJob: true,
      ...twoWayInterviewSettings(languageMode, interviewerNotes, answerReviewGuide)
    } : {}),
    createdAt: new Date().toISOString(),
    ...(baseConfig ? { baseConfig } : {})
  });

  /** Link an AI assessment into the existing workflow without a parallel model. */
  const buildAiInterviewSubstage = (assessmentId, substageId) => ({
    id: substageId,
    name: title.trim(),
    type: 'ai-interview',
    icon: aiDeliveryType === TWO_WAY_VOICE ? 'fa-solid fa-robot' : 'fa-solid fa-video',
    iconClass: aiDeliveryType === TWO_WAY_VOICE ? 'fa-robot' : 'fa-video',
    link: assessmentId,
    afterId: afterId || null,
    departments: buildDepartmentsPayload(),
    duration: aiDeliveryType === TWO_WAY_VOICE ? Math.min(20, duration) : duration,
    deliveryType: aiDeliveryType
  });

  /**
   * Approve an exact manual two-way goal set at creation time.
   * Job validity remains empty until the goals are reviewed for a concrete job.
   */
  const approvalMetadata = async (questions) => {
    if (aiDeliveryType !== TWO_WAY_VOICE) return {};
    const canonical = normalizeQuestions(questions, aiDeliveryType === ONE_WAY_VIDEO)
      .filter((question) => question.text.trim())
      .slice(0, 6)
      .map((question, index) => ({ id: String(question.id || `goal-${index + 1}`), text: question.text.trim().replace(/\s+/g, ' ') }));
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(canonical)));
    const contentHash = Array.from(new Uint8Array(digest)).map((value) => value.toString(16).padStart(2, '0')).join('');
    return {
      approvalStatus: 'approved',
      approvedAt: new Date().toISOString(),
      approvedBy: currentUser.uid,
      contentHash,
      approvedJobIds: {},
      followUpLimit: 2,
      interviewVersion: 2,
      planningSource: 'employer-approved'
    };
  };

  const createStandardInterview = async () => {
    const stageId = 'stage-1';
    const substageId = `interview-${Date.now()}`;
    const payload = {
      id: substageId,
      name: title.trim(),
      type: 'interview',
      icon: 'fa-solid fa-calendar-day',
      iconClass: 'fa-calendar-day',
      afterId: afterId || null,
      duration,
      interviewers: interviewers.reduce((accumulator, interviewerId) => ({
        ...accumulator,
        [interviewerId]: true
      }), {}),
      departments: buildDepartmentsPayload(),
      locationType,
      address: locationType === 'in-person' ? address : ''
    };

    await set(ref(database, `companies/${companyUID}/hiringWorkflow/${stageId}/substages/${substageId}`), payload);
  };

  const createManualAiInterview = async (questions) => {
    const stageId = 'stage-1';
    const substageId = `ai-interview-${Date.now()}`;
    const assessmentId = `assessment-${Date.now()}`;

    const approved = await approvalMetadata(questions);
    await set(
      ref(database, `companies/${companyUID}/assessments/${assessmentId}`),
      buildAiInterviewAssessment({
        assessmentTitle: title.trim(),
        questions,
        totalDuration: duration,
        baseConfig: {
          duration,
          ...(aiDeliveryType === TWO_WAY_VOICE ? {
            assessmentPrompt: interviewerNotes.trim(),
            gradingRubric: answerReviewGuide.trim(),
          } : {}),
          createdAt: new Date().toISOString(),
          generationMode: 'manual',
          ...approved
        }
      })
    );

    await set(
      ref(database, `companies/${companyUID}/hiringWorkflow/${stageId}/substages/${substageId}`),
      buildAiInterviewSubstage(assessmentId, substageId)
    );
  };

  const createGeneratedAiInterview = async () => {
    const stageId = 'stage-1';
    const substageId = `ai-interview-${Date.now()}`;
    const assessmentId = `assessment-${Date.now()}`;

    await set(
      ref(database, `companies/${companyUID}/assessments/${assessmentId}`),
      buildAiInterviewAssessment({
        assessmentTitle: title.trim(),
        questions: [],
        totalDuration: duration,
        baseConfig: {
          numQuestions,
          duration,
          ...(aiDeliveryType === TWO_WAY_VOICE ? {
            assessmentPrompt: interviewerNotes.trim(),
            gradingRubric: answerReviewGuide.trim(),
          } : {}),
          createdAt: new Date().toISOString(),
          generationMode: 'ai',
          sourceModel: 'gpt-4.1-nano',
          ...(aiDeliveryType === TWO_WAY_VOICE ? {
            approvalStatus: 'draft',
            followUpLimit: 2,
            interviewVersion: 2,
            planningSource: 'job-specific-review-required'
          } : {})
        }
      })
    );

    await set(
      ref(database, `companies/${companyUID}/hiringWorkflow/${stageId}/substages/${substageId}`),
      buildAiInterviewSubstage(assessmentId, substageId)
    );
  };

  const save = async () => {
    if (!companyUID) return;

    if (!title.trim()) {
      alert(t('alerts.pleaseEnterName'));
      return;
    }

    if (stepMode === AI_INTERVIEW && questionMode === MANUAL_QUESTIONS) {
      const normalizedManualQuestions = normalizeQuestions(manualQuestions, aiDeliveryType === ONE_WAY_VIDEO);
      const hasQuestionText = normalizedManualQuestions.some((question) => question.text.trim());

      if (!hasQuestionText) {
        alert(t('newInterviewStep.manualQuestionsRequired') || 'Please add at least one interview question.');
        return;
      }

      setManualQuestions(normalizedManualQuestions);
    }

    if (stepMode === AI_INTERVIEW && questionMode === AI_GENERATED_QUESTIONS && numQuestions < 1) {
      alert(t('newInterviewStep.numQuestionsRequired') || 'Please choose at least one question.');
      return;
    }

    setSaving(true);
    try {
      const normalizedManualQuestions = questionMode === MANUAL_QUESTIONS
        ? normalizeQuestions(manualQuestions, aiDeliveryType === ONE_WAY_VIDEO)
        : [];

      if (stepMode === STANDARD_INTERVIEW) {
        await createStandardInterview();
      } else if (questionMode === MANUAL_QUESTIONS) {
        await createManualAiInterview(normalizedManualQuestions);
      } else {
        await createGeneratedAiInterview();
      }

      alert(t('newInterviewStep.created'));
      navigate('/applicants');
    } catch (error) {
      console.error('Save interview step failed', error);
      alert(t('newInterviewStep.failed'));
    } finally {
      setSaving(false);
    }
  };

  const back = () => navigate('/applicants');

  return (
    <div style={{ backgroundColor: 'var(--app-background, #031128)', minHeight: '100vh', color: 'var(--app-text, #FFFFFF)', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', paddingTop: '100px', paddingBottom: '100px', display: 'flex', justifyContent: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
      <style>
        {`
          .new-step-input::placeholder,
          .new-step-select option:first-child {
            color: rgba(var(--app-ink-rgb, 255, 255, 255), 0.4) !important;
          }
          .new-step-select option:not(:first-child) {
            color: var(--app-text, #FFFFFF);
          }
          .chip {
            background-color: var(--app-surface, rgba(255, 255, 255, 0.08));
            border: 1px solid var(--app-border, rgba(255, 255, 255, 0.15));
            color: var(--app-text, #FFFFFF);
            padding: 8px 16px;
            border-radius: 20px;
            cursor: pointer;
            font-family: 'Montserrat';
            font-size: 14px;
            transition: all 0.2s;
          }
          .chip:hover {
            background-color: var(--app-surface-hover, rgba(86, 141, 237, 0.2));
            border-color: #568ded;
          }
          .chip-active {
            background-color: #568ded !important;
            border-color: #568ded !important;
            color: #FFFFFF !important;
          }
          .btn-primary {
            background-color: #568ded;
            color: #FFFFFF;
            border: none;
            border-radius: 25px;
            padding: 12px 32px;
            font-family: 'Montserrat';
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-primary:hover:not(:disabled) {
            background-color: #4a7dd4;
            transform: translateY(-1px);
          }
          .btn-primary:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .info-tooltip {
            position: relative;
            display: inline-flex;
            align-items: center;
            margin-inline-start: 8px;
          }
          .info-tooltip-bubble {
            position: absolute;
            bottom: calc(100% + 10px);
            inset-inline-start: 50%;
            transform: translateX(-50%);
            width: 260px;
            background-color: var(--app-surface, #0f2349);
            border: 1px solid var(--app-border, rgba(255,255,255,0.12));
            border-radius: 10px;
            padding: 10px 12px;
            color: var(--app-text, #FFFFFF);
            font-size: 12px;
            line-height: 1.5;
            box-shadow: 0 10px 25px rgba(0,0,0,0.35);
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.2s ease;
            z-index: 10;
          }
          .info-tooltip:hover .info-tooltip-bubble {
            opacity: 1;
          }
        `}
      </style>

      <div style={{ width: '100%', maxWidth: '900px', paddingInline: '20px', boxSizing: 'border-box' }}>
        <button className="post-job-cta" onClick={back} style={{ marginBottom: '40px' }}>
          <i className={`fa-solid ${isRTL ? 'fa-circle-chevron-right' : 'fa-circle-chevron-left'}`}></i>
          <span>{t('newInterviewStep.backToATS')}</span>
        </button>

        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontWeight: 600, fontSize: '30px', margin: 0 }}>
            {t('newInterviewStep.title')}
          </h1>
          <p style={{ opacity: 0.7, marginTop: '10px', marginBottom: 0 }}>{t('newInterviewStep.subtitle')}</p>
        </div>

        <div style={{ marginBottom: '40px' }}>
          <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.stepTitle')}</p>
          <input
            type="text"
            placeholder={t('newInterviewStep.titlePlaceholder')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="new-step-input"
            style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }}
          />
        </div>

        <div style={{ marginBottom: '40px' }}>
          <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.interviewFormat') || 'Interview Format'}</p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className={`chip ${stepMode === STANDARD_INTERVIEW ? 'chip-active' : ''}`}
              onClick={() => { setStepMode(STANDARD_INTERVIEW); setDuration(60); }}
            >
              {t('newInterviewStep.standardInterview') || 'Standard Interview'}
            </button>
            <button
              type="button"
              className={`chip ${stepMode === AI_INTERVIEW && aiDeliveryType === ONE_WAY_VIDEO ? 'chip-active' : ''}`}
              onClick={() => { setStepMode(AI_INTERVIEW); setAiDeliveryType(ONE_WAY_VIDEO); }}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <i className="fa-solid fa-video" aria-hidden="true" style={{ marginInlineEnd: '8px' }}></i>
              <span>{t('newInterviewStep.aiInterviewOption') || 'One-way AI interview'}</span>
              <span className="info-tooltip">
                <i className="fa-solid fa-circle-info"></i>
                <span className="info-tooltip-bubble">
                  {t('newInterviewStep.aiInterviewTooltip') || 'The person will be shown a question and they respond to it in video format.'}
                </span>
              </span>
            </button>
            <button
              type="button"
              className={`chip ${stepMode === AI_INTERVIEW && aiDeliveryType === TWO_WAY_VOICE ? 'chip-active' : ''}`}
              onClick={() => { setStepMode(AI_INTERVIEW); setAiDeliveryType(TWO_WAY_VOICE); setDuration((value) => Math.min(20, value)); setNumQuestions((value) => Math.min(6, value)); }}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <i className="fa-solid fa-robot" aria-hidden="true" style={{ marginInlineEnd: '8px' }}></i>
              <span>{t('newInterviewStep.twoWayAiInterviewOption') || 'Two-way AI interviewer'}</span>
              <span className="info-tooltip">
                <i className="fa-solid fa-circle-info"></i>
                <span className="info-tooltip-bubble">
                  {t('newInterviewStep.twoWayAiInterviewTooltip') || 'The candidate has a live conversation with an AI interviewer. Recording is explained before the interview starts.'}
                </span>
              </span>
            </button>
          </div>
        </div>

        {stepMode === STANDARD_INTERVIEW ? (
          <>
            <div style={{ marginBottom: '40px' }}>
              <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.assignedInterviewersLabel')}</p>
              <p style={{ margin: 0, marginBottom: '10px', fontSize: '14px', opacity: 0.6 }}>
                <a href="/profile" style={{ color: '#568ded', textDecoration: 'underline' }}>{t('newInterviewStep.addTeamMembers')}</a>
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {teamOptions.map((teamMember) => (
                  <button
                    key={teamMember.uid}
                    type="button"
                    onClick={() => handleToggleInterviewer(teamMember.uid)}
                    className={`chip ${interviewers.includes(teamMember.uid) ? 'chip-active' : ''}`}
                  >
                    {teamMember.name || teamMember.email}
                  </button>
                ))}
                {teamOptions.length === 0 && <span style={{ opacity: 0.7 }}>{t('newInterviewStep.noTeamMembers')}</span>}
              </div>
            </div>

            <div style={{ marginBottom: '40px' }}>
              <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.locationLabel')}</p>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button
                  type="button"
                  className={`chip ${locationType === 'virtual' ? 'chip-active' : ''}`}
                  onClick={() => setLocationType('virtual')}
                >
                  <i className="fa-solid fa-video" style={{ marginInlineEnd: '8px' }}></i>
                  {t('newInterviewStep.virtualRemote')}
                </button>
                <button
                  type="button"
                  className={`chip ${locationType === 'in-person' ? 'chip-active' : ''}`}
                  onClick={() => setLocationType('in-person')}
                >
                  <i className="fa-solid fa-map-marker-alt" style={{ marginInlineEnd: '8px' }}></i>
                  {t('newInterviewStep.inPerson')}
                </button>
              </div>

              {locationType === 'in-person' && (
                <input
                  type="text"
                  placeholder={t('newInterviewStep.enterAddress')}
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  className="new-step-input"
                  style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }}
                />
              )}
            </div>
          </>
        ) : (
          <>
          {aiDeliveryType === TWO_WAY_VOICE && (
            <div style={{ marginBottom: '40px', backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.06))', border: "1px solid var(--app-border, rgba(86, 141, 237, 0.18))", borderRadius: '16px', padding: '20px' }}>
              <p style={{ margin: 0, marginBottom: '6px', fontWeight: 500 }}>{t('newInterviewStep.liveSettingsTitle')}</p>
              <p style={{ margin: 0, marginBottom: '20px', fontSize: '14px', opacity: 0.7 }}>{t('newInterviewStep.liveSettingsHelp')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label htmlFor="two-way-language" style={{ display: 'block', marginBottom: '10px' }}>{t('newInterviewStep.languageLabel')}</label>
                  <select id="two-way-language" value={languageMode} onChange={(event) => setLanguageMode(event.target.value)} className="new-step-select" style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }}>
                    <option value="candidate-choice">{t('newInterviewStep.languageCandidateChoice')}</option>
                    <option value="en">{t('newInterviewStep.languageEnglish')}</option>
                    <option value="ar">{t('newInterviewStep.languageArabic')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="two-way-duration" style={{ display: 'block', marginBottom: '10px' }}>{t('newInterviewStep.interviewDurationLabel')}</label>
                  <select id="two-way-duration" value={duration} onChange={(event) => setDuration(parseInt(event.target.value, 10))} className="new-step-select" style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }}>
                    <option value={10}>10 {t('newInterviewStep.minutes')}</option>
                    <option value={15}>15 {t('newInterviewStep.minutes')}</option>
                    <option value={20}>20 {t('newInterviewStep.minutes')}</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="two-way-notes" style={{ display: 'block', marginBottom: '6px' }}>{t('newInterviewStep.interviewerNotesLabel')}</label>
                <p style={{ margin: '0 0 10px', fontSize: '13px', opacity: 0.65 }}>{t('newInterviewStep.interviewerNotesHelp')}</p>
                <textarea id="two-way-notes" rows="4" value={interviewerNotes} onChange={(event) => setInterviewerNotes(event.target.value)} placeholder={t('newInterviewStep.interviewerNotesPlaceholder')} className="new-step-input" style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }} />
              </div>
              <div>
                <label htmlFor="two-way-review" style={{ display: 'block', marginBottom: '6px' }}>{t('newInterviewStep.answerReviewLabel')}</label>
                <p style={{ margin: '0 0 10px', fontSize: '13px', opacity: 0.65 }}>{t('newInterviewStep.answerReviewHelp')}</p>
                <textarea id="two-way-review" rows="4" value={answerReviewGuide} onChange={(event) => setAnswerReviewGuide(event.target.value)} placeholder={t('newInterviewStep.answerReviewPlaceholder')} className="new-step-input" style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }} />
              </div>
            </div>
          )}
          <div style={{ marginBottom: '40px' }}>
            <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.questionSetup') || 'Question Setup'}</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <button
                type="button"
                className={`chip ${questionMode === MANUAL_QUESTIONS ? 'chip-active' : ''}`}
                onClick={() => setQuestionMode(MANUAL_QUESTIONS)}
              >
                {t('newInterviewStep.manualQuestions') || 'Add questions manually'}
              </button>
              <button
                type="button"
                className={`chip ${questionMode === AI_GENERATED_QUESTIONS ? 'chip-active' : ''}`}
                onClick={() => setQuestionMode(AI_GENERATED_QUESTIONS)}
              >
                {t('newInterviewStep.generateQuestions') || 'Generate questions with AI'}
              </button>
            </div>

            {questionMode === MANUAL_QUESTIONS ? (
              <div style={{ backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.06))', border: "1px solid var(--app-border, rgba(86, 141, 237, 0.18))", borderRadius: '16px', padding: '20px' }}>
                <AssessmentQuestionBuilder
                  questions={manualQuestions}
                  onChange={setManualQuestions}
                  t={t}
                  isAiInterview
                  isOneWayInterviewerBuilder={aiDeliveryType === ONE_WAY_VIDEO}
                  videoBannerText={aiDeliveryType === TWO_WAY_VOICE
                    ? (t('newInterviewStep.twoWayManualQuestionsDescription') || 'Add the main questions you want the AI to ask about. All answers are spoken live, so there is no answer type to choose.')
                    : (t('newInterviewStep.manualQuestionsDescription') || 'Add the interview questions here. Candidates will answer each question by recording a short video.')}
                />
              </div>
            ) : (
              <div style={{ backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.06))', border: "1px solid var(--app-border, rgba(86, 141, 237, 0.18))", borderRadius: '16px', padding: '20px' }}>
                <div className="ae-video-banner" style={{ marginBottom: '20px' }}>
                  <i className="fa-solid fa-wand-magic-sparkles"></i>
                  <p>{aiDeliveryType === TWO_WAY_VOICE
                    ? (t('newInterviewStep.twoWayAiGenerationDescription') || 'AI will create questions for the job. You must check and save them before an invitation is sent.')
                    : (t('newInterviewStep.aiGenerationDescription') || 'Questions will be generated for each candidate from their CV and the job description when you send the interview link.')}</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                  <div>
                    <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.numQuestions') || 'Number of questions'}</p>
                    <input
                      type="number"
                      min="1"
                      max={aiDeliveryType === TWO_WAY_VOICE ? 6 : 20}
                      value={numQuestions}
                      onChange={(event) => setNumQuestions(parseInt(event.target.value, 10) || 1)}
                      className="new-step-input"
                      style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  {aiDeliveryType !== TWO_WAY_VOICE && <div>
                    <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.interviewDurationLabel') || 'Interview Duration'}</p>
                    <input
                      type="number"
                      min="1"
                      max={aiDeliveryType === TWO_WAY_VOICE ? 20 : 180}
                      value={duration}
                      onChange={(event) => setDuration(parseInt(event.target.value, 10) || 1)}
                      className="new-step-input"
                      style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>}
                </div>
              </div>
            )}
          </div>
          </>
        )}

        {(stepMode === STANDARD_INTERVIEW || (stepMode === AI_INTERVIEW && questionMode === MANUAL_QUESTIONS && aiDeliveryType !== TWO_WAY_VOICE)) && (
          <div style={{ marginBottom: '40px' }}>
            <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.interviewDurationLabel')}</p>
            <select
              value={duration}
              onChange={(event) => setDuration(parseInt(event.target.value, 10))}
              className="new-step-select"
              style={{ width: '200px', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }}
            >
              {stepMode === AI_INTERVIEW && aiDeliveryType === TWO_WAY_VOICE ? (
                <>
                  <option value={10}>10 {t('newInterviewStep.minutes')}</option>
                  <option value={15}>15 {t('newInterviewStep.minutes')}</option>
                  <option value={20}>20 {t('newInterviewStep.minutes')}</option>
                </>
              ) : (
                <>
                  <option value={30}>30 {t('newInterviewStep.minutes')}</option>
                  <option value={45}>45 {t('newInterviewStep.minutes')}</option>
                  <option value={60}>60 {t('newInterviewStep.minutes')}</option>
                  <option value={90}>90 {t('newInterviewStep.minutes')}</option>
                </>
              )}
            </select>
          </div>
        )}

        <div style={{ marginBottom: '40px' }}>
          <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.departmentsLabel')}</p>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <button
              type="button"
              className={`chip ${departments.length === 0 ? 'chip-active' : ''}`}
              onClick={() => {
                setDepartments([]);
                setShowDepartmentDropdown(false);
              }}
            >
              {t('newInterviewStep.allDepartments')}
            </button>
            <button
              type="button"
              className={`chip ${showDepartmentDropdown || departments.length > 0 ? 'chip-active' : ''}`}
              onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
            >
              {t('newInterviewStep.selectDepartments')}
            </button>
          </div>

          {(departments.length > 0 || showDepartmentDropdown) && (
            <div className="department-dropdown" style={{ position: 'relative', width: '400px', maxWidth: '100%' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px', justifyContent: 'flex-start' }}>
                {departments.map((department) => (
                  <div key={department} style={{ backgroundColor: '#568ded', color: '#FFFFFF', padding: '4px 10px', borderRadius: '12px', fontSize: '13px', fontFamily: 'Montserrat', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {translateDepartment(department, i18n.language)}
                    <i
                      className="fa-solid fa-times"
                      style={{ cursor: 'pointer', fontSize: '10px' }}
                      onClick={() => setDepartments((prev) => prev.filter((item) => item !== department))}
                    ></i>
                  </div>
                ))}
              </div>

              <div style={{ position: 'relative', width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", borderRadius: '8px', padding: '8px 12px', minHeight: '150px', maxHeight: '250px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
                {departmentOptions.map((department) => (
                  <div
                    key={department}
                    style={{ display: 'grid', gridTemplateColumns: '18px 1fr', alignItems: 'center', columnGap: '6px', padding: '6px 8px', cursor: 'pointer', color: 'var(--app-text, #FFFFFF)', fontFamily: 'Montserrat', fontSize: '13px', borderRadius: '4px', transition: 'background-color 0.2s', textAlign: 'left', justifyItems: 'start' }}
                    onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.1))'; }}
                    onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = 'transparent'; }}
                    onClick={() => {
                      setDepartments((prev) => (
                        prev.includes(department)
                          ? prev.filter((item) => item !== department)
                          : [...prev, department]
                      ));
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={departments.includes(department)}
                      onChange={() => {}}
                      style={{ accentColor: '#568ded', width: '16px', height: '16px', margin: 0, cursor: 'pointer' }}
                    />
                    <span style={{ lineHeight: '16px' }}>{translateDepartment(department, i18n.language)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '40px' }}>
          <p style={{ margin: 0, marginBottom: '10px' }}>{t('newInterviewStep.addAfterLabel')}</p>
          <select
            value={afterId}
            onChange={(event) => setAfterId(event.target.value)}
            className="new-step-select"
            style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '14px', outline: 'none' }}
          >
            <option value="">{t('newInterviewStep.selectStep')}</option>
            {afterOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
          <button className="btn-primary" onClick={save} disabled={saving}>
            <span style={{ marginInlineEnd: '8px' }}>
              {saving ? t('newInterviewStep.saving') : t('newInterviewStep.createStep')}
            </span>
            <i className={`fa-solid ${isRTL ? 'fa-circle-chevron-left' : 'fa-circle-chevron-right'}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewInterviewStep;
